import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/hooks/use-permissions";
import type { PermissionKey } from "@/lib/permissions";
import { RouteFallback } from "./route-states";

interface RequirePermissionProps {
  /** The screen's `*.view` key. */
  permission: PermissionKey;
  children: ReactNode;
}

/**
 * Renders a screen only for an admin allowed to open it.
 *
 * The sidebar already hides screens an admin cannot open; this covers the
 * other ways in — a typed URL, a bookmark, a link shared by a colleague.
 * Legacy guarded its menu only, so any URL opened any screen.
 *
 * Refusing here is a courtesy: the API refuses the data anyway. What it adds
 * is an honest page instead of a screen of 403 errors.
 */
export function RequirePermission({ permission, children }: RequirePermissionProps) {
  const { can, isLoading, error, refetch } = usePermissions();

  if (isLoading) return <RouteFallback />;

  if (error) {
    return (
      <ErrorState
        title="Couldn’t load your permissions"
        error={error}
        onRetry={() => void refetch()}
      />
    );
  }

  if (!can(permission)) return <NoAccess />;

  return <>{children}</>;
}

/**
 * Says who to ask rather than what is missing — the key means nothing to the
 * person, and an owner is who can grant it.
 */
export function NoAccess() {
  return (
    <EmptyState
      icon={Lock}
      title="You don’t have access to this screen"
      description="If you need it for your work, ask an owner to give you access on the Roles & permissions screen."
      action={
        <Button asChild variant="outline" size="sm">
          <Link to="/dashboard">Go to dashboard</Link>
        </Button>
      }
    />
  );
}
